import type { ArcadeIcon } from '@arcade/web/icons/react'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import { spacing } from '../../theme'
import ArcadeHelpText, { combineAriaDescribedBy } from '../Input/ArcadeHelpText'
import { Flex } from '../layout/Flex'
import RadioCell, { RadioCellProps } from '../RadioCell/RadioCell'
import RadioImage, { RadioImageProps } from '../RadioImage/RadioImage'
import RadioInputCard, { RadioInputCardProps } from '../RadioInputCard/RadioInputCard'
import ArcadeRadioInline, { ArcadeRadioInlineProps } from '../RadioInputInline/ArcadeRadioInline'

/**
 * Notes on accessibility:
 * The role="radiogroup" is required to use the aria-requried attribute
 * There is no need for aria-checked because we are using native html input type=radio
 * The name prop on each radio input is needed so when one is selected, others in group are automatically deselected
 * The aria-label on the fieldset replaces the need for a <legend> element
 * Pass in an explicit aria-label, or pass in an aria-labelledby if you have a label element that will label this radio group (fieldset).
 */

export interface ArcadeRadioGroupProps extends React.FieldsetHTMLAttributes<HTMLFieldSetElement> {
  name: string
  options: RadioInputCardProps[] | RadioCellProps[] | ArcadeRadioInlineProps[] | RadioImageProps[]
  radioStyle: 'cell' | 'input-card' | 'inline' | 'image'
  required?: boolean
  direction?: 'column' | 'row'
  helpText?: string
  HelpTextIcon?: ArcadeIcon
  errorText?: string
  disabledText?: string
  isInvalid?: boolean
  disabled?: boolean
  CustomOptionsWrapper?: React.ComponentType<{ children: React.ReactNode }>
}

const ArcadeRadioGroup = ({
  name,
  options,
  radioStyle,
  required = false,
  direction = 'column',
  helpText,
  HelpTextIcon,
  CustomOptionsWrapper,
  errorText,
  disabledText,
  isInvalid = false,
  disabled = false,
  'aria-describedby': ariaDescribedBy,
  ...restProps
}: ArcadeRadioGroupProps) => {
  const helpTextId = useHelpTextId({ helpText, errorText, disabledText })

  /**
   * If 'radioStyle' is 'cell', the items will be flush with each other.
   * Else, the gap is 16px.
   */
  const radioGroupGap = () => {
    if (radioStyle === 'cell') {
      return direction === 'column' ? spacing.xl : spacing.xxl
    } else {
      return spacing.m
    }
  }

  const OptionsWrapper = CustomOptionsWrapper ?? Flex

  return (
    <>
      <fieldset
        aria-required={required}
        role="radiogroup"
        disabled={disabled}
        aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
        {...restProps}
      >
        <OptionsWrapper gap={radioGroupGap()} flexDirection={direction}>
          {options.map(({ label, ...restRadioProps }) => {
            if (radioStyle === 'cell') {
              return (
                <RadioCell name={name} label={label} key={`${name}-${label}`} disabled={disabled} {...restRadioProps} />
              )
            } else if (radioStyle === 'input-card') {
              return (
                <RadioInputCard
                  name={name}
                  label={label}
                  key={`${name}-${label}`}
                  disabled={disabled}
                  {...restRadioProps}
                />
              )
            } else if (radioStyle === 'inline') {
              return (
                <ArcadeRadioInline
                  name={name}
                  label={label}
                  key={`${name}-${label}`}
                  disabled={disabled}
                  {...restRadioProps}
                />
              )
            } else if (radioStyle === 'image') {
              return (
                <RadioImage
                  name={name}
                  label={label}
                  key={`${name}-${label}`}
                  disabled={disabled}
                  {...(restRadioProps as Omit<RadioImageProps, 'name' | 'label' | 'disabled'>)}
                />
              )
            }
            return null
          })}
        </OptionsWrapper>
      </fieldset>
      <ArcadeHelpText
        helpTextId={helpTextId}
        helpText={helpText}
        HelpTextIcon={HelpTextIcon}
        isInvalid={isInvalid}
        errorText={errorText}
        disabled={disabled}
        disabledText={disabledText}
        marginTop="l"
      />
    </>
  )
}

export default ArcadeRadioGroup
